// client/src/components/pages/AmendmentApprovals/index.tsx (converted from .js, ST-00221)
import { useState, useEffect, useCallback, type ReactElement } from 'react';
import type { AxiosResponse } from 'axios';
import {
  FaCheck,
  FaTimes,
  FaSpinner,
  FaClock,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaClipboardCheck,
  FaUndo,
  FaTrashAlt,
} from 'react-icons/fa';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { PageHeader } from '../../../types/commonComponents';
import type {
  AmendmentApprovalRow,
  ApprovalListResponse,
  ApprovalPayload,
  ApprovalStatus,
  ApprovalTicketDetail,
  ApproveResponse,
} from '../../../types/clientPages/amendmentApprovals';

/** The roles that may withdraw anyone's submission (as the server's withdraw rule). */
const ADMIN_TIER_ROLES = ['administrator', 'global-admin'] as const;

type RequestError = Error & { response?: { status?: number; data?: { message?: string } } };
type ActionKind = 'approve' | 'reject' | 'withdraw';
type StatusFilter = ApprovalStatus | 'all';

const STATUS_STYLES: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 ring-amber-300',
  approved: 'bg-green-100 text-green-800 ring-green-300',
  rejected: 'bg-red-100 text-red-800 ring-red-300',
  withdrawn: 'bg-gray-100 text-gray-600 ring-gray-300',
};

const STATUS_ICONS: Record<ApprovalStatus, ReactElement> = {
  pending: <FaClock className="w-3 h-3" />,
  approved: <FaCheck className="w-3 h-3" />,
  rejected: <FaTimes className="w-3 h-3" />,
  withdrawn: <FaUndo className="w-3 h-3" />,
};

const formatDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleString();
};

const parseJson = <T,>(value: string | T | null | undefined): T | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as T;
  } catch (_) {
    return null;
  }
};

interface ApprovalDetailPanelProps {
  approval: AmendmentApprovalRow;
  onApprove: (approvalId: number, reviewNotes: string) => void;
  onReject: (approvalId: number, reviewNotes: string) => void;
  onWithdraw: (approvalId: number, reviewNotes: string) => void;
  /** The submitter or an admin-tier user (AMD-22): only they see Withdraw. */
  canWithdraw: boolean;
  actionLoading: ActionKind | null;
  isSelected: boolean;
  onToggleSelect: (approvalId: number) => void;
}

const ApprovalDetailPanel = ({
  approval,
  onApprove,
  onReject,
  onWithdraw,
  canWithdraw,
  actionLoading,
  isSelected,
  onToggleSelect,
}: ApprovalDetailPanelProps) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [expanded, setExpanded] = useState(false);

  if (!approval) return null;

  const payload = parseJson<ApprovalPayload>(approval.AmendmentPayload);

  // Inline-edit batches (bulk-individual) carry their own values per ticket;
  // every other payload applies one `updates` object to all its tickets.
  const perTicketUpdates = Array.isArray(payload?.amendments)
    ? new Map(payload!.amendments!.map((a) => [a.ticketUID, a.updates || {}]))
    : null;
  const changeFieldsFor = (ticketUID: string) =>
    Object.entries(
      (perTicketUpdates ? perTicketUpdates.get(ticketUID) : payload?.updates) || {}
    ).filter(([k]) => k !== 'AmendmentNotes');
  const changeFields = changeFieldsFor(approval.TicketUID);
  // Only show a "before" value we actually loaded; a missing column is not "(blank)"
  const beforeOf = (row: Record<string, unknown>, field: string) =>
    Object.prototype.hasOwnProperty.call(row, field)
      ? row[field] !== null && row[field] !== undefined
        ? String(row[field])
        : '(blank)'
      : null;
  const bulkUIDs = approval.IsBulk ? parseJson<string[]>(approval.BulkTicketUIDs) : null;
  const ticketLabel = approval['Ticket No'] || approval.TicketUID?.slice(0, 12);

  return (
    <article
      aria-label={`Approval ${ticketLabel}`}
      className={`border rounded-lg bg-surface shadow-sm overflow-hidden transition-colors ${isSelected ? 'border-primary-400 ring-2 ring-primary-200' : 'border-gray-200'}`}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {approval.Status === 'pending' && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(approval.AmendmentApprovalID)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
            )}
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {ticketLabel}
                {bulkUIDs && (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    + {bulkUIDs.length - 1} more tickets
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Submitted by{' '}
                <span className="font-medium text-gray-700">{approval.SubmittedBy}</span> on{' '}
                {formatDate(approval.SubmittedAt)}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[approval.Status] ?? ''}`}
          >
            {STATUS_ICONS[approval.Status]}
            {approval.Status}
          </span>
        </div>
      </div>

      {/* Changes */}
      <div className="px-5 py-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 mb-3">
          Proposed Changes
        </div>

        {payload?.amendmentNotes && (
          <div className="mb-3 text-xs text-gray-600 italic bg-gray-50 rounded px-3 py-2 border border-gray-100">
            "{payload.amendmentNotes}"
          </div>
        )}

        {perTicketUpdates && bulkUIDs && bulkUIDs.length > 1 && (
          <p className="mb-3 text-xs text-gray-500">
            Inline edits: each ticket has its own values. Showing{' '}
            {approval['Ticket No'] || 'the first ticket'}; the rest are listed under Affected
            Tickets.
          </p>
        )}

        <div className="space-y-2">
          {changeFields.map(([field, newValue]) => {
            const beforeStr = beforeOf(approval, field);
            const afterStr = newValue !== null && newValue !== undefined ? String(newValue) : null;

            return (
              <div
                key={field}
                className="rounded-md border border-gray-100 bg-gray-50/50 px-3 py-2"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  {field}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {beforeStr !== null && (
                    <>
                      <span
                        className="text-red-500/80 line-through truncate max-w-[40%]"
                        title={beforeStr}
                      >
                        {beforeStr}
                      </span>
                      <FaArrowRight className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                    </>
                  )}
                  <span
                    className="text-green-700 font-medium truncate max-w-[40%]"
                    title={afterStr || '(blank)'}
                  >
                    {afterStr || '(blank)'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Current ticket state (collapsible) */}
        {!!(approval['Dispatch Location'] || approval['Receipt Location']) && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Current State{' '}
              {expanded ? (
                <FaChevronUp className="w-2.5 h-2.5" />
              ) : (
                <FaChevronDown className="w-2.5 h-2.5" />
              )}
            </button>
            {expanded && (
              <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  ['Ticket No', approval['Ticket No']],
                  ['Ticket Type', approval['Ticket Type']],
                  ['Dispatch Location', approval['Dispatch Location']],
                  ['Receipt Location', approval['Receipt Location']],
                  ['Product', approval['Product']],
                  ['Nett Weight', approval['Nett Weight']],
                  ['Haulier', approval['Haulier']],
                  ['Reg No', approval['Reg No']],
                  ['Delivery No', approval['Delivery No']],
                  ['Order No', approval['Order No']],
                ].map(([label, val]) => (
                  <div key={String(label)}>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-400">
                      {String(label)}
                    </dt>
                    <dd className="font-medium text-gray-700">
                      {val === null || val === undefined ? '—' : String(val)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}

        {/* Bulk ticket list */}
        {bulkUIDs && bulkUIDs.length > 1 && (
          <details className="mt-3">
            <summary className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 cursor-pointer hover:text-gray-600">
              {bulkUIDs.length} Affected Tickets
            </summary>
            <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                    <th className="px-2 py-1.5 text-left font-semibold">Ticket No</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Dispatch</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Receipt</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(
                    approval.BulkTicketDetails ||
                    bulkUIDs.map((uid): ApprovalTicketDetail => ({ TicketUID: uid }))
                  ).map((ticket) => (
                    <tr key={ticket.TicketUID} className="hover:bg-gray-50/50">
                      <td className="px-2 py-1.5 font-medium text-gray-700 whitespace-nowrap">
                        {ticket['Ticket No'] || ticket.TicketUID?.slice(0, 12)}
                      </td>
                      <td
                        className="px-2 py-1.5 text-gray-600 truncate max-w-[120px]"
                        title={ticket['Dispatch Location'] ?? undefined}
                      >
                        {ticket['Dispatch Location'] || '—'}
                      </td>
                      <td
                        className="px-2 py-1.5 text-gray-600 truncate max-w-[120px]"
                        title={ticket['Receipt Location'] ?? undefined}
                      >
                        {ticket['Receipt Location'] || '—'}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {changeFieldsFor(ticket.TicketUID).map(([field, newValue]) => {
                            const beforeStr = beforeOf(ticket, field);
                            const afterStr =
                              newValue !== null && newValue !== undefined
                                ? String(newValue)
                                : '(blank)';
                            return (
                              <span
                                key={field}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 text-[10px]"
                                title={
                                  beforeStr !== null
                                    ? `${field}: ${beforeStr} → ${afterStr}`
                                    : `${field}: → ${afterStr}`
                                }
                              >
                                <span className="font-medium text-gray-500">{field}:</span>
                                {beforeStr !== null && (
                                  <>
                                    <span className="text-red-500/80 line-through">
                                      {beforeStr}
                                    </span>
                                    <FaArrowRight className="w-2 h-2 text-gray-300" />
                                  </>
                                )}
                                <span className="text-green-700 font-medium">{afterStr}</span>
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        {/* Review info (for already-reviewed) */}
        {approval.ReviewedBy && (
          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <span className="font-medium text-gray-700">
              {approval.Status === 'approved'
                ? 'Approved'
                : approval.Status === 'withdrawn'
                  ? 'Withdrawn'
                  : 'Rejected'}
            </span>{' '}
            by {approval.ReviewedBy} on {formatDate(approval.ReviewedAt)}
            {approval.ReviewNotes && <p className="mt-1 italic">"{approval.ReviewNotes}"</p>}
          </div>
        )}
      </div>

      {/* Action buttons (only for pending) */}
      {approval.Status === 'pending' && (
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 space-y-3">
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Review notes (optional)…"
            rows={2}
            className="block w-full text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onApprove(approval.AmendmentApprovalID, reviewNotes)}
              disabled={!!actionLoading}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'approve' ? (
                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FaCheck className="w-3.5 h-3.5" />
              )}
              Approve & Apply
            </button>
            <button
              type="button"
              onClick={() => onReject(approval.AmendmentApprovalID, reviewNotes)}
              disabled={!!actionLoading}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'reject' ? (
                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FaTimes className="w-3.5 h-3.5" />
              )}
              Reject
            </button>
            {/* Withdraw: the submitter or an admin-tier user only, as the server (AMD-22) */}
            {canWithdraw && (
              <button
                type="button"
                onClick={() => onWithdraw(approval.AmendmentApprovalID, reviewNotes)}
                disabled={!!actionLoading}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
                title="Withdraw this pending approval"
              >
                {actionLoading === 'withdraw' ? (
                  <FaSpinner className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FaUndo className="w-3.5 h-3.5" />
                )}
                Withdraw
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

const AmendmentApprovals = () => {
  const { user, hasAnyAppRole } = useAuth();
  const isAdminTier = hasAnyAppRole(ADMIN_TIER_ROLES);
  const canWithdraw = (a: AmendmentApprovalRow) =>
    isAdminTier || (!!user?.username && a.SubmittedBy === user.username);

  const [approvals, setApprovals] = useState<AmendmentApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A partly applied approval (its failed tickets are back in the queue)
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [pagination, setPagination] = useState<ApprovalListResponse['pagination']>({
    page: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 0,
  });
  const [actionLoading, setActionLoading] = useState<ActionKind | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchApprovals = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      setNotice(null);
      setSelectedIds(new Set());
      try {
        const res: AxiosResponse<ApprovalListResponse> = await api.amendmentApprovals.list({
          status: statusFilter,
          page,
          limit: 25,
        });
        setApprovals(res.data.data);
        setPagination(res.data.pagination);
      } catch (caught) {
        const err = caught as RequestError;
        setError(err.response?.data?.message || err.message || 'Failed to load approvals');
      } finally {
        setLoading(false);
      }
    },
    [statusFilter]
  );

  const fetchPendingCount = useCallback(async () => {
    try {
      const res: AxiosResponse<{ count: number }> = await api.amendmentApprovals.pendingCount();
      setPendingCount(res.data.count);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);
  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  const handleApprove = async (approvalId: number, reviewNotes: string) => {
    setActionLoading('approve');
    setNotice(null);
    try {
      const res: AxiosResponse<ApproveResponse> = await api.amendmentApprovals.approve(
        approvalId,
        reviewNotes
      );
      await fetchApprovals(pagination.page);
      await fetchPendingCount();
      // Partial success: the failed tickets went back to the queue as a new pending approval;
      // say so (fetchApprovals clears the banners, so set it after)
      const data = res?.data;
      if (data?.failed?.length) {
        const requeued =
          data.requeuedApprovalId && !(data.message || '').includes(`#${data.requeuedApprovalId}`)
            ? ` The failed tickets are back in the queue as approval #${data.requeuedApprovalId}.`
            : '';
        setNotice(`${data.message || 'Some tickets were not applied.'}${requeued}`);
      }
    } catch (caught) {
      const err = caught as RequestError;
      setError(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (approvalId: number, reviewNotes: string) => {
    setActionLoading('reject');
    try {
      await api.amendmentApprovals.reject(approvalId, reviewNotes);
      await fetchApprovals(pagination.page);
      await fetchPendingCount();
    } catch (caught) {
      const err = caught as RequestError;
      setError(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleWithdraw = async (approvalId: number, reviewNotes: string) => {
    setActionLoading('withdraw');
    try {
      await api.amendmentApprovals.withdraw(approvalId, reviewNotes);
      await fetchApprovals(pagination.page);
      await fetchPendingCount();
    } catch (caught) {
      const err = caught as RequestError;
      setError(err.response?.data?.message || 'Failed to withdraw');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const pendingIds = approvals
      .filter((a) => a.Status === 'pending')
      .map((a) => a.AmendmentApprovalID);
    if (selectedIds.size === pendingIds.length && pendingIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  const handleBulkAction = async (action: 'reject' | 'withdraw') => {
    if (selectedIds.size === 0) return;
    const label = action === 'withdraw' ? 'withdraw' : 'reject';
    if (
      !window.confirm(`Are you sure you want to ${label} ${selectedIds.size} selected approval(s)?`)
    )
      return;

    setBulkActionLoading(true);
    try {
      await api.amendmentApprovals.bulkAction(Array.from(selectedIds), action, `Bulk ${label}`);
      await fetchApprovals(pagination.page);
      await fetchPendingCount();
    } catch (caught) {
      const err = caught as RequestError;
      setError(err.response?.data?.message || `Failed to ${label} selected approvals`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const pendingApprovals = approvals.filter((a) => a.Status === 'pending');
  const allPendingSelected =
    pendingApprovals.length > 0 && selectedIds.size === pendingApprovals.length;
  // Withdraw Selected only when every selected submission may be withdrawn by this user
  const canWithdrawSelected = approvals
    .filter((a) => selectedIds.has(a.AmendmentApprovalID))
    .every(canWithdraw);

  const tabs: { value: StatusFilter; label: string; count?: number }[] = [
    { value: 'pending', label: 'Pending', count: pendingCount },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'withdrawn', label: 'Withdrawn' },
    { value: 'all', label: 'All' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaClipboardCheck}
        title="Amendment Approvals"
        subtitle="Review and approve pending amendment requests"
      />

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                statusFilter === tab.value
                  ? 'bg-surface text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {!!tab.count && tab.count > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold bg-amber-500 text-white rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Bulk action toolbar */}
        {statusFilter === 'pending' && pendingApprovals.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-surface border border-gray-200 rounded-lg shadow-sm">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </label>

            {selectedIds.size > 0 && (
              <>
                <div className="h-4 w-px bg-gray-200" />
                {canWithdrawSelected && (
                  <button
                    onClick={() => handleBulkAction('withdraw')}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    {bulkActionLoading ? (
                      <FaSpinner className="w-3 h-3 animate-spin" />
                    ) : (
                      <FaUndo className="w-3 h-3" />
                    )}
                    Withdraw Selected
                  </button>
                )}
                <button
                  onClick={() => handleBulkAction('reject')}
                  disabled={bulkActionLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50 transition-colors"
                >
                  {bulkActionLoading ? (
                    <FaSpinner className="w-3 h-3 animate-spin" />
                  ) : (
                    <FaTrashAlt className="w-3 h-3" />
                  )}
                  Reject Selected
                </button>
              </>
            )}
          </div>
        )}

        {/* Partly applied approval */}
        {notice && (
          <div
            role="status"
            className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center justify-between"
          >
            <span>{notice}</span>
            <button
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="text-amber-500 hover:text-amber-700"
            >
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <FaSpinner className="w-5 h-5 animate-spin mr-2" /> Loading approvals…
          </div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FaClock className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">
              No {statusFilter === 'all' ? '' : statusFilter} approvals found
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {approvals.map((a) => (
              <ApprovalDetailPanel
                key={a.AmendmentApprovalID}
                approval={a}
                onApprove={handleApprove}
                onReject={handleReject}
                onWithdraw={handleWithdraw}
                canWithdraw={canWithdraw(a)}
                actionLoading={actionLoading}
                isSelected={selectedIds.has(a.AmendmentApprovalID)}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchApprovals(pagination.page - 1)}
              className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchApprovals(pagination.page + 1)}
              className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmendmentApprovals;
